import React from 'react';
import type { ComponentFieldDefinition } from '@troia-v3/types';
import { FormField } from '@/shared/components/ui/FormField';
import { FormSection } from '@/shared/components/ui/FormSection';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';
import { Select } from '@/shared/components/ui/Select';
import { Switch } from '@/shared/components/ui/Switch';
import { ColorPickerField } from './widgets/ColorPickerField';
import { ImageUploaderField } from './widgets/ImageUploaderField';
import { VideoUploaderField } from './widgets/VideoUploaderField';
import { ReferenceSelector } from './widgets/ReferenceSelector';
import { MultiSelect } from '@/shared/components/ui/MultiSelect';
import { ListFieldEditor } from './widgets/ListFieldEditor';
import { FunnelStepCascadeSelector } from './widgets/FunnelStepCascadeSelector';

interface DynamicFieldRendererProps {
  fields: ComponentFieldDefinition[];
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  groupLabel?: string;
}

/**
 * Renders a dynamic form based on ComponentFieldDefinition schema.
 * Maps each FieldType to the appropriate UI widget.
 */
export const DynamicFieldRenderer: React.FC<DynamicFieldRendererProps> = ({
  fields,
  values,
  onChange,
  groupLabel,
}) => {
  // Group fields by their 'group' property
  const grouped = groupFieldsByCategory(fields);

  if (groupLabel) {
    return (
      <div className="space-y-4">
        {renderFields(fields, values, onChange)}
      </div>
    );
  }

  // If no groups, render flat
  if (grouped.size <= 1 && !grouped.has(undefined as unknown as string)) {
    return (
      <div className="space-y-4">
        {renderFields(fields, values, onChange)}
      </div>
    );
  }

  // Render grouped sections
  return (
    <div className="space-y-6">
      {Array.from(grouped.entries()).map(([group, groupFields]) => (
        <FormSection
          key={group || 'general'}
          title={group || 'Geral'}
          collapsible
        >
          <div className="space-y-4">
            {renderFields(groupFields, values, onChange)}
          </div>
        </FormSection>
      ))}
    </div>
  );
};

function groupFieldsByCategory(fields: ComponentFieldDefinition[]): Map<string, ComponentFieldDefinition[]> {
  const groups = new Map<string, ComponentFieldDefinition[]>();

  const sorted = [...fields].sort((a, b) => (a.order || 0) - (b.order || 0));

  for (const field of sorted) {
    const group = field.group || 'Geral';
    if (!groups.has(group)) {
      groups.set(group, []);
    }
    groups.get(group)!.push(field);
  }

  return groups;
}

function shouldShowField(
  field: ComponentFieldDefinition,
  values: Record<string, unknown>
): boolean {
  if (!field.showIf) return true;

  const { field: condField, operator, value } = field.showIf;
  const currentValue = values[condField];

  switch (operator) {
    case 'eq':
      return currentValue === value;
    case 'neq':
      return currentValue !== value;
    case 'exists':
      return currentValue !== undefined && currentValue !== null && currentValue !== '';
    default:
      return true;
  }
}

function renderFields(
  fields: ComponentFieldDefinition[],
  values: Record<string, unknown>,
  onChange: (name: string, value: unknown) => void
) {
  return fields
    .filter((field) => shouldShowField(field, values))
    .map((field) => (
      <RenderSingleField
        key={field.name}
        field={field}
        value={values[field.name]}
        onChange={(val) => onChange(field.name, val)}
        allValues={values}
        onChangeAll={onChange}
      />
    ));
}

interface SingleFieldProps {
  field: ComponentFieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  allValues: Record<string, unknown>;
  onChangeAll: (name: string, value: unknown) => void;
}

const RenderSingleField: React.FC<SingleFieldProps> = ({
  field,
  value,
  onChange,
  allValues,
  onChangeAll,
}) => {
  switch (field.type) {
    case 'text':
    case 'url':
      return (
        <FormField label={field.label} description={field.description} required={field.required}>
          <Input
            type={field.type === 'url' ? 'url' : 'text'}
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
        </FormField>
      );

    case 'textarea':
      return (
        <FormField label={field.label} description={field.description} required={field.required}>
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
          />
        </FormField>
      );

    case 'number':
      return (
        <FormField label={field.label} description={field.description} required={field.required}>
          <Input
            type="number"
            value={value !== undefined && value !== null ? String(value) : ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : undefined)}
            placeholder={field.placeholder}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        </FormField>
      );

    case 'boolean':
      return (
        <Switch
          label={field.label}
          description={field.description}
          checked={!!value}
          onChange={(checked) => onChange(checked)}
        />
      );

    case 'select':
      return (
        <FormField label={field.label} description={field.description} required={field.required}>
          <Select
            value={(value as string) || ''}
            onChange={(val) => onChange(val)}
            options={(field.options || []).map((opt) => ({
              label: opt.label,
              value: String(opt.value),
            }))}
            placeholder={field.placeholder || 'Selecione...'}
          />
        </FormField>
      );

    case 'multiselect':
      return (
        <FormField label={field.label} description={field.description} required={field.required}>
          <MultiSelect
            options={(field.options || []).map((opt) => ({
              label: opt.label,
              value: String(opt.value),
            }))}
            selectedValues={(value as string[]) || []}
            onChange={(values) => onChange(values.filter(v => v !== ''))}
            placeholder={field.placeholder || 'Selecione...'}
          />
        </FormField>
      );

    case 'color':
      return (
        <ColorPickerField
          label={field.label}
          description={field.description}
          value={(value as string) || '#000000'}
          onChange={onChange}
        />
      );

    case 'image':
      return (
        <ImageUploaderField
          label={field.label}
          description={field.description}
          value={value as string}
          onChange={onChange}
          multiple={false}
        />
      );

    case 'images':
      return (
        <ImageUploaderField
          label={field.label}
          description={field.description}
          value={value as string[]}
          onChange={onChange}
          multiple
        />
      );

    case 'video':
      return (
        <VideoUploaderField
          label={field.label}
          description={field.description}
          value={value as string | undefined}
          onChange={onChange}
        />
      );

    case 'reference':
      return (
        <ReferenceSelector
          field={field}
          value={value}
          onChange={onChange}
        />
      );

    case 'funnelStep':
      return (
        <FunnelStepCascadeSelector
          field={field}
          values={allValues}
          onChange={onChangeAll}
        />
      );

    case 'list':
      return (
        <ListFieldEditor
          field={field}
          value={(value as Record<string, unknown>[]) || []}
          onChange={onChange}
          allValues={allValues}
          onChangeAll={onChangeAll}
        />
      );

    case 'object':
      if (!field.subFields) return null;
      return (
        <FormSection title={field.label} description={field.description} collapsible>
          <DynamicFieldRenderer
            fields={field.subFields}
            values={(value as Record<string, unknown>) || {}}
            onChange={(subName, subValue) => {
              const current = (value as Record<string, unknown>) || {};
              onChange({ ...current, [subName]: subValue });
            }}
            groupLabel={field.label}
          />
        </FormSection>
      );

    case 'richtext':
      return (
        <FormField label={field.label} description={field.description} required={field.required}>
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder}
            rows={6}
          />
        </FormField>
      );

    default:
      return null;
  }
};
